<template>
  <v-container
    fluid
    class="px-6"
  >
    <v-row
      justify="center"
    >
      <v-col cols="12">
        <h3 class="headline text-center primary--text my-5 font-weight-bold">
          {{ this.$t('Laboratory Network Management Suite') }}
        </h3>
      </v-col>

      <v-col
        md="4"
        lg="4"
        cols="12"
        class="pe-lg-16 pt-0"
      >
        <div class="mb-8">
          <h3 class="title mb-3 primary--text">
            {{ this.$t('What is LabCompanion.net?') }}
          </h3>
          <p class="mb-3 primary--text">
            {{ this.$t(`LabCompanion is a unique open-source software for One Health laboratory network management. It helps laboratory administrators to simplify and optimize performance, facilitate follow-up, share, and store documentation within labs at district, regional, central, country, and One Health levels.`) }}
          </p>
        </div>

        <hr class="mb-7 opacity-50">

        <div>
          <h3 class="title mt-0 mb-3 primary--text">
            {{ this.$t('Advantages') }}
          </h3>
          <ul class="primary--text">
            <li class="mb-1">
              {{ this.$t('Possible to access the interface via smartphones even in remote places through 3G/4G') }}
            </li>
            <li class="mb-1">
              {{ this.$t('Modular system with different topics addressed') }}
            </li>
            <li class="mb-1">
              {{ this.$t('Strong user management interface to enable/disable functionalities and geographical coverage') }}
            </li>
            <li class="mb-1">
              {{ this.$t('Simultaneously analyze workload, previous supervision results, shortages and training programs') }}
              <ul class="mt-1">
                <li>
                  {{ this.$t('Comprehensive laboratory overview') }}
                </li>
              </ul>
            </li>
            <li class="mb-1">
              {{ this.$t('Each country selects modules and defines') }}
            </li>
          </ul>
        </div>

        <h3 class="subtitle-1 primary--text mt-8">
          Are you aiming to improve lab network coordination in your country and strengthen individual laboratory capacities? Contact us at
          <a href="mailto:labcompanion@iqls.net">labcompanion@iqls.net</a>
          to register!
        </h3>
      </v-col>

      <v-col
        md="4"
        lg="4"
        cols="12"
      >
        <v-card class="pa-4">
          <v-card-title class="primary--text text--lighten-2 pb-0">
            {{ $t('Sign in to') }}
          </v-card-title>
          <v-card-title class="primary--text pt-0">
            {{ $t('IQLS Laboratory Companion') }}
          </v-card-title>
          <v-card-text class="mt-8">
            <!--            <v-alert-->
            <!--              v-model="loginError"-->
            <!--              dense-->
            <!--              type="error"-->
            <!--              dismissible-->
            <!--            >-->
            <!--              {{ $t('The username or password is not correct.') }}-->
            <!--            </v-alert>-->

            <ValidationObserver
              ref="loginObserver"
              v-slot="{ validate, reset }"
            >
              <form @submit.prevent="submit">
                <ValidationProvider
                  v-slot="{ errors }"
                  name="email"
                  rules="required|email"
                >
                  <v-text-field
                    v-model="email"
                    :error-messages="errors"
                    :label="$t('E-mail')"
                    type="email"
                    autocomplate="username"
                    prepend-inner-icon="mdi-email"
                    required
                  />
                </ValidationProvider>

                <ValidationProvider
                  v-slot="{ errors }"
                  name="password"
                  rules="required|min:6"
                >
                  <v-text-field
                    v-model="password"
                    :error-messages="errors"
                    prepend-inner-icon="mdi-lock"
                    :append-icon="passwordShow ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="passwordShow ? 'text' : 'password'"
                    :label="$t('Password')"
                    autocomplete="current-password"
                    required
                    @click:append="passwordShow = !passwordShow"
                  />
                </ValidationProvider>

                <div class="d-flex align-center justify-space-between my-4">
                  <v-checkbox
                    v-model="rememberMe"
                    :label="$t('Remember me')"
                    type="checkbox"
                  />
                  <!--                  <v-btn-->
                  <!--                    class="text-none"-->
                  <!--                    color="primary"-->
                  <!--                    text-->
                  <!--                    :to="{name: 'ForgotPassword'}"-->
                  <!--                  >-->
                  <!--                    {{ $t('Forgot password') }}?-->
                  <!--                  </v-btn>-->
                </div>

                <v-btn
                  block
                  color="primary"
                  type="submit"
                  class="mr-4"
                >
                  {{ $t('Sign in') }}
                </v-btn>
              </form>
            </ValidationObserver>
          </v-card-text>
        </v-card>

        <v-img
          class="mt-10 rounded"
          max-width="100%"
          src="@/assets/login-page-graphs.png"
        />
      </v-col>

      <v-col
        md="4"
        lg="4"
        cols="12"
        class="pt-0 ps-lg-15"
      >
        <h3 class="title mb-3 primary--text">
          {{ this.$t('We currently offer 4 initial modules:') }}
        </h3>

        <v-expansion-panels
          v-model="panel"
          :disabled="disabled"
          multiple
          dark
        >
          <v-expansion-panel>
            <v-expansion-panel-header color="primary">
              1. {{ this.$t('Training management module') }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <ul class="my-3">
                <li>
                  {{ this.$t(`Administer training programs and sessions`) }}
                </li>
                <li>
                  {{ this.$t(`Store generic and session - specific materials`) }}
                </li>
                <li>
                  {{ this.$t(`Track training history for staff(training hours, pre - and post - test scores, and the number of days since the last training)`) }}
                </li>
                <li>
                  {{ this.$t(`Manage requirements in terms of minimal proficiency and continuous training policies`) }}
                </li>
              </ul>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header color="primary">
              2. {{ this.$t('Supervision management module') }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <ul class="my-3">
                <li>
                  {{ this.$t(`Organize a repository of all supervision types and checklists in the country`) }}
                </li>
                <li>
                  {{ this.$t(`Organize and schedule future supervisions`) }}
                </li>
                <li>
                  {{ this.$t(`Make supervision results accessible to authorized users`) }}
                </li>
                <li>
                  {{ this.$t(`Generate, monitor, follow up and close recommendations`) }}
                </li>
                <li>
                  {{ this.$t(`Visualize and analyze progress using tables and graphs`) }}
                </li>
              </ul>
            </v-expansion-panel-content>
          </v-expansion-panel>


          <v-expansion-panel>
            <v-expansion-panel-header color="primary">
              3. {{ this.$t('Equipment management module') }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <ul class="my-3">
                <li>
                  {{ this.$t(`Equipment inventory indicates the name, brand, status, and age of all equipment in all labs`) }}
                </li>
                <li>
                  {{ this.$t(`Equipment maintenance shows all performed, pending, and delayed calibration or certification for relevant equipment`) }}
                </li>
                <li>
                  {{ this.$t(`LabCompanion will notify you in advance about the upcoming date of calibration or certification`) }}
                </li>
              </ul>
            </v-expansion-panel-content>
          </v-expansion-panel>


          <v-expansion-panel>
            <v-expansion-panel-header color="primary">
              4. {{ this.$t('Workload and Shortage Management module') }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <ul class="my-3">
                <li>
                  {{ this.$t(`Workload Management tracks the number of analysis performed on monthly basis`) }}
                </li>
                <li>
                  {{ this.$t(`Shortage Management will help you closely monitor shortage-selected analyses, and track reasons for shortages`) }}
                </li>
                <li>
                  {{ this.$t(`LabCompanion allows you to display these data in tables and graphs`) }}
                </li>
              </ul>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div class="mt-12">
          <h3 class="title mb-3 primary--text">
            {{ this.$t('IQLS LabCompanion offer') }}
          </h3>
          <ul class="primary--text">
            <li class="mb-2">
              {{ this.$t('A detailed user manual') }}
            </li>
            <li class="mb-2">
              {{ this.$t('Video tutorials') }}
            </li>
            <li class="mb-2">
              {{ this.$t('Introductory brochure') }}
            </li>
            <li class="mb-2">
              {{ this.$t('Training and mentoring (online or F2F) tailored for your country') }}
            </li>
            <li class="mb-2">
              {{ this.$t('Support for installation of new modules') }}
            </li>
            <li class="mb-2">
              {{ this.$t('Hosting, maintenance, and helpdesk') }}
            </li>
          </ul>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
    import FormValidation from "../mixins/FormValidation";
    import {mapActions} from 'vuex'

    export default {
        name: "Login",
        mixins: [FormValidation],
        data() {
            return {
                loginError: false,
                email: '',
                password: '',
                passwordShow: false,
                rememberMe: false,
            }
        },

        methods: {
            ...mapActions([
                'login'
            ]),

            async submit() {
                let valid = await this.$refs.loginObserver.validate();
                if (!valid) return;

                let loginData = {
                    email: this.email,
                    password: this.password,
                    rememberMe: this.rememberMe
                };

                this.login({loginData})
                    .then(() => {
                        let redirectUrl = this.$route?.query?.redirect;
                        if (redirectUrl) {
                            this.$router.push(redirectUrl)
                        } else {
                            this.$router.push({name: "Home"})
                        }
                    })
                    .catch(() => {
                        this.loginError = true
                    })

            },
        }
    }
</script>

<style scoped>

</style>
